import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  attendanceAdjustmentsApiErrorMessage,
  createAttendanceAdjustments,
  listAttendanceAdjustments,
  type AttendanceAdjustmentEffective,
  type AttendanceAdjustmentRow,
  type AttendanceAdjustmentType,
} from "@/lib/hr/attendanceAdjustmentsStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, ClipboardList, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const HOUR_OPTIONS = Array.from({ length: 13 }, (_, i) => i); // 0–12
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i); // 0–59

type TimeOption = { value: string; label: string; search: string };

type DraftLine = {
  adjustmentType: AttendanceAdjustmentType;
  hoursPart: string;
  minutesPart: string;
  reason: string;
  startOtType: AttendanceAdjustmentType;
  startOtHours: string;
  startOtMinutes: string;
  endOtType: AttendanceAdjustmentType;
  endOtHours: string;
  endOtMinutes: string;
};

function emptyDraft(): DraftLine {
  return {
    adjustmentType: "add",
    hoursPart: "0",
    minutesPart: "0",
    reason: "",
    startOtType: "add",
    startOtHours: "0",
    startOtMinutes: "0",
    endOtType: "add",
    endOtHours: "0",
    endOtMinutes: "0",
  };
}

function clampMinutes(total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  return Math.min(779, Math.round(Math.abs(total)));
}

function minutesToParts(totalMinutes: number): { hoursPart: string; minutesPart: string } {
  const total = clampMinutes(totalMinutes);
  return {
    hoursPart: String(Math.floor(total / 60)),
    minutesPart: String(total % 60),
  };
}

function signedToTypeAndParts(signedMinutes: number): {
  type: AttendanceAdjustmentType;
  hoursPart: string;
  minutesPart: string;
} {
  const type: AttendanceAdjustmentType = signedMinutes < 0 ? "deduct" : "add";
  const parts = minutesToParts(Math.abs(signedMinutes));
  return { type, ...parts };
}

function reasonFromRows(rows: AttendanceAdjustmentRow[]): string {
  for (const row of rows) {
    const r = row.reason?.trim();
    if (r) return r;
  }
  return "";
}

function draftFromExisting(params: {
  enableSplitOt: boolean;
  rows: AttendanceAdjustmentRow[];
  effective: AttendanceAdjustmentEffective;
}): DraftLine {
  const base = emptyDraft();
  base.reason = reasonFromRows(params.rows);

  if (params.enableSplitOt) {
    const start = signedToTypeAndParts(params.effective.startOtMinutes);
    const end = signedToTypeAndParts(params.effective.endOtMinutes);
    return {
      ...base,
      startOtType: start.type,
      startOtHours: start.hoursPart,
      startOtMinutes: start.minutesPart,
      endOtType: end.type,
      endOtHours: end.hoursPart,
      endOtMinutes: end.minutesPart,
    };
  }

  const general = signedToTypeAndParts(params.effective.minutes);
  return {
    ...base,
    adjustmentType: general.type,
    hoursPart: general.hoursPart,
    minutesPart: general.minutesPart,
  };
}

function parseDurationParts(hoursPart: string, minutesPart: string): number | null {
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);
  if (
    !Number.isInteger(hours) ||
    hours < 0 ||
    hours > 12 ||
    !Number.isInteger(minutes) ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  return hours * 60 + minutes;
}

function SearchableTimePartPicker({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: TimeOption[];
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [cycle, setCycle] = useState(0);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (next) setCycle((c) => c + 1);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "glass-input h-10 w-full justify-between px-3 font-normal text-sm",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="truncate">{selected?.label ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        sideOffset={4}
      >
        <Command key={cycle} shouldFilter>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList className="max-h-56">
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = value === opt.value;
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.search}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-3.5 w-3.5 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                    {opt.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export type AttendanceAdjustmentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** One or more employees; bulk save when length > 1. */
  employeeIds: string[];
  employeeName: string | null;
  /** One or more dates (Y-m-d); bulk save when length > 1. */
  adjustmentDates: string[];
  /** When true (work policy `enable_split_ot`), show Start OT / End OT cards. */
  enableSplitOt?: boolean;
  /** `add` skips loading existing rows (always blank form). `edit` loads the first employee+date. */
  mode?: "add" | "edit";
  canCreate: boolean;
  /** Called after create so the parent can refresh daily nets. */
  onChanged?: () => void;
};

export function AttendanceAdjustmentDialog({
  open,
  onOpenChange,
  employeeIds,
  employeeName,
  adjustmentDates,
  enableSplitOt = false,
  mode = "edit",
  canCreate,
  onChanged,
}: AttendanceAdjustmentDialogProps) {
  const [draft, setDraft] = useState<DraftLine>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const primaryEmployeeId = employeeIds[0] ?? "";
  const primaryDate = adjustmentDates[0] ?? "";
  const isBulkEmployees = employeeIds.length > 1;
  const isBulkDates = adjustmentDates.length > 1;
  const isBulk = isBulkEmployees || isBulkDates;
  const employeeFieldLabel = isBulkEmployees
    ? `${employeeIds.length} employees selected`
    : employeeName?.trim() || "—";
  const dateFieldLabel = isBulkDates
    ? `${adjustmentDates.length} dates selected`
    : primaryDate || "—";

  const hourOptions = useMemo<TimeOption[]>(
    () =>
      HOUR_OPTIONS.map((h) => ({
        value: String(h),
        label: String(h),
        search: `${h} ${h}h hours hour`,
      })),
    [],
  );
  const minuteOptions = useMemo<TimeOption[]>(
    () =>
      MINUTE_OPTIONS.map((m) => ({
        value: String(m),
        label: String(m),
        search: `${m} ${m}m minutes minute`,
      })),
    [],
  );

  const loadExisting = useCallback(async () => {
    if (mode === "add" || isBulk || !primaryEmployeeId || !primaryDate) {
      setDraft(emptyDraft());
      setIsEditing(false);
      return;
    }
    setLoadingExisting(true);
    try {
      const result = await listAttendanceAdjustments({
        employeeId: primaryEmployeeId,
        adjustmentDate: primaryDate,
      });
      if (result.rows.length > 0) {
        setIsEditing(true);
        setDraft(
          draftFromExisting({
            enableSplitOt,
            rows: result.rows,
            effective: result.effective,
          }),
        );
      } else {
        setIsEditing(false);
        setDraft(emptyDraft());
      }
    } catch (e) {
      toast.error(attendanceAdjustmentsApiErrorMessage(e));
      setIsEditing(false);
      setDraft(emptyDraft());
    } finally {
      setLoadingExisting(false);
    }
  }, [mode, isBulk, primaryEmployeeId, primaryDate, enableSplitOt]);

  useEffect(() => {
    if (!open) return;
    void loadExisting();
  }, [open, loadExisting]);

  const close = () => {
    if (saving || loadingExisting) return;
    onOpenChange(false);
  };

  const save = async () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create attendance adjustments.");
      return;
    }
    if (employeeIds.length < 1) {
      toast.error("Select at least one employee.");
      return;
    }
    if (adjustmentDates.length < 1) {
      toast.error("Select at least one date.");
      return;
    }

    let lines: Array<{
      adjustmentType?: AttendanceAdjustmentType;
      minutes?: number;
      startOtMinutes?: number | null;
      endOtMinutes?: number | null;
      startOtAdjustmentType?: AttendanceAdjustmentType;
      endOtAdjustmentType?: AttendanceAdjustmentType;
      reason?: string | null;
    }>;

    if (enableSplitOt) {
      const startOt = parseDurationParts(draft.startOtHours, draft.startOtMinutes);
      const endOt = parseDurationParts(draft.endOtHours, draft.endOtMinutes);
      if (startOt == null || endOt == null) {
        toast.error("Select valid hours (0–12) and minutes (0–59) for Start OT and End OT.");
        return;
      }
      if (startOt < 1 && endOt < 1) {
        toast.error("Enter at least 1 minute for Start OT or End OT.");
        return;
      }
      lines = [
        {
          startOtMinutes: startOt,
          endOtMinutes: endOt,
          startOtAdjustmentType: draft.startOtType,
          endOtAdjustmentType: draft.endOtType,
          reason: draft.reason.trim() || null,
        },
      ];
    } else {
      const totalMinutes = parseDurationParts(draft.hoursPart, draft.minutesPart);
      if (totalMinutes == null) {
        toast.error("Select valid hours (0–12) and minutes (0–59).");
        return;
      }
      if (totalMinutes < 1) {
        toast.error("Select at least 1 minute.");
        return;
      }
      lines = [
        {
          adjustmentType: draft.adjustmentType,
          minutes: totalMinutes,
          reason: draft.reason.trim() || null,
        },
      ];
    }

    setSaving(true);
    try {
      let lastMessage = "Attendance adjustment recorded.";
      let savedCount = 0;
      for (const id of employeeIds) {
        for (const date of adjustmentDates) {
          const result = await createAttendanceAdjustments({
            employeeId: id,
            adjustmentDate: date,
            lines,
          });
          lastMessage = result.message;
          savedCount += 1;
        }
      }
      toast.success(
        isBulk
          ? `Adjustment saved for ${savedCount} employee-date combination${savedCount === 1 ? "" : "s"}.`
          : lastMessage,
      );
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(attendanceAdjustmentsApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
              <ClipboardList className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">
                {mode === "edit" || isEditing ? "Edit Adjustment" : "Add Adjustment"}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                {enableSplitOt
                  ? "Set Start OT and End OT independently with + or −."
                  : mode === "edit" || isEditing
                    ? "Update the adjustment for this employee on the selected date."
                    : "Add or deduct time for the selected employees and dates."}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-xl border border-border/60 bg-muted/10 p-4 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/40 pb-2 text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-foreground">{isBulkEmployees ? "Employees:" : "Employee:"}</span>
                <span className="font-extrabold text-primary text-sm">{employeeFieldLabel}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {enableSplitOt ? "Form uses Start/End OT from work policy." : "Form uses general OT hours."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
              <span className="text-[11px] font-semibold text-muted-foreground">Selection:</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border/60 bg-background">
                <span className="text-primary font-bold">{employeeIds.length}</span>
                <span className="text-muted-foreground text-[10px] ml-1">
                  {employeeIds.length === 1 ? "employee" : "employees"}
                </span>
              </span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border/60 bg-background">
                <span className="text-primary font-bold">{isBulkDates ? adjustmentDates.length : dateFieldLabel}</span>
                <span className="text-muted-foreground text-[10px] ml-1">
                  {isBulkDates ? (adjustmentDates.length === 1 ? "date" : "dates") : "date"}
                </span>
              </span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border/60 bg-background">
                <span className="text-primary font-bold">{enableSplitOt ? "Start/End OT" : "General OT"}</span>
                <span className="text-muted-foreground text-[10px] ml-1">form</span>
              </span>
              {isBulk ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-primary/30 bg-primary/10">
                  <span className="text-primary font-bold">{employeeIds.length * adjustmentDates.length}</span>
                  <span className="text-muted-foreground text-[10px] ml-1">
                    {employeeIds.length * adjustmentDates.length === 1 ? "record" : "records"}
                  </span>
                </span>
              ) : null}
            </div>
          </div>

          {loadingExisting ? (
            <div className="rounded-xl border border-border/60 bg-muted/10 px-3 py-10 text-center text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading adjustment…
              </span>
            </div>
          ) : canCreate ? (
            enableSplitOt ? (
              <div className="space-y-3">
                <div className="hidden md:grid md:grid-cols-2 gap-3 px-1">
                  <Label className="text-xs font-bold text-foreground">Start OT (SAOT)</Label>
                  <Label className="text-xs font-bold text-foreground">End OT (EAOT)</Label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-border/60 p-3 space-y-3 bg-background/50">
                    <Label className="text-xs text-muted-foreground md:sr-only">Start OT (SAOT)</Label>
                    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)_minmax(0,1fr)] gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-foreground">Type</Label>
                        <Select
                          value={draft.startOtType}
                          onValueChange={(v) =>
                            setDraft((d) => ({ ...d, startOtType: v as AttendanceAdjustmentType }))
                          }
                        >
                          <SelectTrigger className="glass-input h-10 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="add">+</SelectItem>
                            <SelectItem value="deduct">-</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-foreground">Hours</Label>
                        <SearchableTimePartPicker
                          value={draft.startOtHours}
                          onChange={(v) => setDraft((d) => ({ ...d, startOtHours: v }))}
                          options={hourOptions}
                          placeholder="Hours"
                          searchPlaceholder="Search hours…"
                          disabled={saving || loadingExisting}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-foreground">Minutes</Label>
                        <SearchableTimePartPicker
                          value={draft.startOtMinutes}
                          onChange={(v) => setDraft((d) => ({ ...d, startOtMinutes: v }))}
                          options={minuteOptions}
                          placeholder="Minutes"
                          searchPlaceholder="Search minutes…"
                          disabled={saving || loadingExisting}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-border/60 p-3 space-y-3 bg-background/50">
                    <Label className="text-xs text-muted-foreground md:sr-only">End OT (EAOT)</Label>
                    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)_minmax(0,1fr)] gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-foreground">Type</Label>
                        <Select
                          value={draft.endOtType}
                          onValueChange={(v) =>
                            setDraft((d) => ({ ...d, endOtType: v as AttendanceAdjustmentType }))
                          }
                        >
                          <SelectTrigger className="glass-input h-10 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="add">+</SelectItem>
                            <SelectItem value="deduct">-</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-foreground">Hours</Label>
                        <SearchableTimePartPicker
                          value={draft.endOtHours}
                          onChange={(v) => setDraft((d) => ({ ...d, endOtHours: v }))}
                          options={hourOptions}
                          placeholder="Hours"
                          searchPlaceholder="Search hours…"
                          disabled={saving || loadingExisting}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-foreground">Minutes</Label>
                        <SearchableTimePartPicker
                          value={draft.endOtMinutes}
                          onChange={(v) => setDraft((d) => ({ ...d, endOtMinutes: v }))}
                          options={minuteOptions}
                          placeholder="Minutes"
                          searchPlaceholder="Search minutes…"
                          disabled={saving || loadingExisting}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-foreground">Reason / Note</Label>
                  <Input
                    className="glass-input h-10"
                    value={draft.reason}
                    onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
                    placeholder="e.g. Missed punch correction"
                    maxLength={255}
                    disabled={saving || loadingExisting}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="hidden md:grid md:grid-cols-[7rem_minmax(7rem,1fr)_minmax(7rem,1fr)_minmax(12rem,1.6fr)] gap-3 px-1">
                  <Label className="text-xs font-bold text-foreground">Type</Label>
                  <Label className="text-xs font-bold text-foreground">Hours</Label>
                  <Label className="text-xs font-bold text-foreground">Minutes</Label>
                  <Label className="text-xs font-bold text-foreground">Reason / Note</Label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[7rem_minmax(7rem,1fr)_minmax(7rem,1fr)_minmax(12rem,1.6fr)] gap-3 items-end rounded-xl border border-border/60 p-3 md:border-0 md:p-0 md:rounded-none bg-background/50">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground md:sr-only">Type</Label>
                    <Select
                      value={draft.adjustmentType}
                      onValueChange={(v) =>
                        setDraft((d) => ({ ...d, adjustmentType: v as AttendanceAdjustmentType }))
                      }
                    >
                      <SelectTrigger className="glass-input h-10 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="add">+</SelectItem>
                        <SelectItem value="deduct">-</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground md:sr-only">Hours</Label>
                    <SearchableTimePartPicker
                      value={draft.hoursPart}
                      onChange={(v) => setDraft((d) => ({ ...d, hoursPart: v }))}
                      options={hourOptions}
                      placeholder="Hours"
                      searchPlaceholder="Search hours…"
                      disabled={saving || loadingExisting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground md:sr-only">Minutes</Label>
                    <SearchableTimePartPicker
                      value={draft.minutesPart}
                      onChange={(v) => setDraft((d) => ({ ...d, minutesPart: v }))}
                      options={minuteOptions}
                      placeholder="Minutes"
                      searchPlaceholder="Search minutes…"
                      disabled={saving || loadingExisting}
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label className="text-xs text-muted-foreground md:sr-only">Reason / Note</Label>
                    <Input
                      className="glass-input h-10"
                      value={draft.reason}
                      onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
                      placeholder="e.g. Missed punch correction"
                      maxLength={255}
                      disabled={saving || loadingExisting}
                    />
                  </div>
                </div>
              </div>
            )
          ) : (
            <p className="text-sm text-muted-foreground">You don’t have permission to save adjustments.</p>
          )}
        </div>

        <DialogFooter className="pt-4 border-t border-border/50 gap-2">
          <Button type="button" variant="outline" onClick={close} disabled={saving || loadingExisting}>
            Cancel
          </Button>
          {canCreate ? (
            <Button
              type="button"
              onClick={() => void save()}
              disabled={saving || loadingExisting}
              className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEditing || mode === "edit" ? "Update Adjustment" : "Save Adjustment"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
